/* @flow */
import * as React from 'react';
import ReactModal from 'react-modal';
import Row from 'components/Row';
import Text from 'components/Text';
import Heading from 'components/Heading';
import Link from 'components/Link';

import AdminIntegration from 'admin/components/AdminIntegration';
import AdminIntegrationEmbed from 'admin/components/AdminIntegrationEmbed';
import AdminIntegrationsContainer from 'admin/containers/AdminIntegrationsContainer';
import AdminIntegratedToolsUpsellModal from 'admin/components/AdminIntegratedToolsUpsellModal';
import AdminRemindPlanIntegrationContainer from 'admin/containers/AdminRemindPlanIntegrationContainer.tsx';
import OrganizationIntegrationPermissions from 'admin/components/OrganizationIntegrationPermissions';
import PartnerIntegration from 'admin/components/PartnerIntegration';
import PartnerIntegrationModal from 'admin/components/PartnerIntegrationModal';

import { partition } from 'lodash';
import CurrentUser from 'auth/CurrentUser';

import './styles/AdminIntegratedToolsView.scss';

import LinkConstants from 'constants/LinkConstants';

type Props = {
  data: any,
  organizationUUID: string,
};

type State = {
  showPartnerIntegration?: *,
  showAdminControlledIntegrationUpsell?: *,
};

class AdminIntegratedToolsView extends React.Component<Props, State> {
  state: State = {};

  showUpsellModal = (int: *) => this.setState({ showAdminControlledIntegrationUpsell: int });
  closeUpsellModal = () => this.setState({ showAdminControlledIntegrationUpsell: undefined });

  showPartnerModal = (int: *) => this.setState({ showPartnerIntegration: int });
  closePartnerModal = () => this.setState({ showPartnerIntegration: undefined });

  render() {
    const { organizationUUID } = this.props;
    const currentUser = CurrentUser.getUser();
    const can_toggle_VC_integrations =
      currentUser && currentUser.hasFeature('video_composer_integrations.web.jun-20');
    const hasGenericLTISetup =
      currentUser && currentUser.hasFeature('generic_lti_setup_flow.web.dec-20');

    return (
      <OrganizationIntegrationPermissions organizationUUID={organizationUUID}>
        {({ canSee, canEdit, canEditATPI, loading }) => {
          if (loading) {
            return null;
          }

          if (!canSee) {
            return <div>not found</div>;
          }

          const adminIntegrationProps = integration => ({
            integration,
            canEdit,
            organizationUUID,
            key: integration.providerKey,
            hasGenericLTISetup,
            onActionUnauthorized: () => this.showUpsellModal(integration),
          });

          return (
            <div>
              <ReactModal isOpen={!!this.state.showAdminControlledIntegrationUpsell}>
                <AdminIntegratedToolsUpsellModal
                  onRequestHide={this.closeUpsellModal}
                  organizationUUID={organizationUUID}
                  integration={this.state.showAdminControlledIntegrationUpsell}
                />
              </ReactModal>
              <ReactModal isOpen={!!this.state.showPartnerIntegration}>
                <PartnerIntegrationModal
                  onRequestHide={this.closePartnerModal}
                  integration={this.state.showPartnerIntegration}
                />
              </ReactModal>
              <AdminIntegrationsContainer
                fetchPolicy="cache-and-network"
                organizationUUID={organizationUUID}>
                {({ adminIntegrations, partnerIntegrations }) => {
                  const [remindPlanIntegrations, defaultIntegrations] = partition(
                    partnerIntegrations,
                    'isAdminToggleable'
                  );
                  return (
                    <>
                      <Row size="xxxlarge">
                        <Heading>App integrations</Heading>
                        <Row size="small">
                          <Text lineHeight="large" size="small" color="graphite" element="div">
                            Give your community access to these apps from the Remind composer.{' '}
                            <Link
                              href={LinkConstants.ADMIN_INTEGRATIONS_HELP}
                              target="_blank"
                              weight="semibold">
                              Learn More
                            </Link>
                          </Text>
                        </Row>

                        {can_toggle_VC_integrations && remindPlanIntegrations.length > 0 && (
                          <Row data-test="remind-plan-integrations">
                            <Row size="small">
                              <Text size="small" weight="semibold">
                                Remind Hub integrations
                              </Text>
                              <Text lineHeight="large" size="small" color="graphite" element="div">
                                These integrations can be enabled for everyone in your organization.
                              </Text>
                            </Row>
                            {remindPlanIntegrations.map(integration => (
                              <AdminRemindPlanIntegrationContainer
                                {...{ ...adminIntegrationProps(integration), canEdit: canEditATPI }}
                              />
                            ))}
                          </Row>
                        )}

                        <Row data-test="admin-integrations">
                          <Row size="small">
                            <Text size="small" weight="semibold">
                              Premium integrations
                            </Text>
                            <Text lineHeight="large" size="small" color="graphite" element="div">
                              These integrations are available as add-ons to Remind Hub.
                            </Text>
                          </Row>
                          {adminIntegrations.map(integration => (
                            <AdminIntegration {...adminIntegrationProps(integration)} />
                          ))}
                          {hasGenericLTISetup && (
                            <AdminIntegrationEmbed
                              remindPlanIntegrations={adminIntegrations}
                              organizationUUID={this.props.organizationUUID}
                            />
                          )}
                        </Row>
                      </Row>

                      <Row size="large" data-test="partner-integrations">
                        <Text size="small" weight="semibold">
                          Default integrations
                        </Text>
                        <Row size="small">
                          <Text lineHeight="large" size="small" color="graphite" element="div">
                            These integrations are included for everyone in your organization.
                          </Text>
                        </Row>
                        {defaultIntegrations.map(integration => (
                          <PartnerIntegration
                            integration={integration}
                            organizationUUID={organizationUUID}
                            onClick={() => this.showPartnerModal(integration)}
                            key={integration.providerKey}
                          />
                        ))}
                      </Row>
                    </>
                  );
                }}
              </AdminIntegrationsContainer>
            </div>
          );
        }}
      </OrganizationIntegrationPermissions>
    );
  }
}

export default AdminIntegratedToolsView;
